import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { API } from '../../utils/api';
import WhiskeyLogo from './WhiskeyLogo';
import WhiskeyChat from './WhiskeyChat';

interface AppShellProps {
  children: React.ReactNode;
  user: { firstName: string; lastName: string; email: string } | null;
  signOut?: () => void;
}

const NAV_ITEMS = [
  {
    name: 'Dashboard',
    path: '/',
    icon: (
      <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="6" height="6" rx="1" /><rect x="11" y="3" width="6" height="6" rx="1" />
        <rect x="3" y="11" width="6" height="6" rx="1" /><rect x="11" y="11" width="6" height="6" rx="1" />
      </svg>
    ),
  },
  {
    name: 'Frameworks',
    path: '/frameworks',
    badge: '10 Tracks',
    icon: (
      <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
        <circle cx="10" cy="4" r="2.2" />
        <circle cx="4" cy="15" r="2.2" />
        <circle cx="16" cy="15" r="2.2" />
        <path d="M8.8 5.8L5.2 13M11.2 5.8L14.8 13M6.2 15h7.6" />
      </svg>
    ),
  },
  {
    name: 'Production',
    path: '/production',
    badge: 'Evals & Observability',
    icon: (
      <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 13.5l3.5-3.5 4 4 6.5-6.5" />
        <path d="M13.5 7.5H17V11" />
        <circle cx="4" cy="4" r="1.5" />
      </svg>
    ),
  },
  {
    name: 'Missions',
    path: '/prompt-quest',
    icon: (
      <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
        <circle cx="4.5" cy="5" r="1.7" /><circle cx="15.5" cy="15" r="1.7" /><circle cx="14" cy="5" r="1.3" />
        <path d="M6.1 5.6C9 6.5 8 10 11 10.6c2 .4 2.2 2 2.3 2.9" />
      </svg>
    ),
  },
  {
    name: 'Playground',
    path: '/playground',
    icon: (
      <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
        <rect x="2.5" y="3.5" width="15" height="13" rx="2" /><path d="M6 8l2.5 2-2.5 2" /><path d="M10.5 12h3.5" />
      </svg>
    ),
  },
  {
    name: 'Leaderboard',
    path: '/leaderboard',
    icon: (
      <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 16V11" /><path d="M10 16V6" /><path d="M16 16V9" />
      </svg>
    ),
  },
];

const SECONDARY_ITEMS = [
  { name: 'Review', path: '/review' },
  { name: 'Challenges', path: '/challenges' },
  { name: 'Reference', path: '/reference' },
  { name: 'Badges & Rewards', path: '/gamification' },
  { name: 'Profile', path: '/profile' },
  { name: 'Help/FAQ', path: '/help-faq' },
];

const AppShell: React.FC<AppShellProps> = ({ children, user, signOut }) => {
  const location = useLocation();
  const [level, setLevel] = useState(1);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem('ws_sidebar_collapsed');
    return saved !== null ? saved === 'true' : true; // Default to collapsed as requested!
  });

  const toggleSidebar = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('ws_sidebar_collapsed', String(next));
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    API.get('/progress')
      .then((response: any) => {
        if (!cancelled && response && response.length > 0) {
          setLevel(response[0].level || 1);
        }
      })
      .catch(() => {
        /* keep default level on error */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const initials = user ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() : '';
  const isActive = (path: string) => (path === '/' ? location.pathname === '/' : location.pathname.startsWith(path));

  return (
    <div className="ws-shell" style={{ minHeight: '100vh', display: 'flex', background: 'var(--paper)', position: 'relative' }}>
      {/* Collapsible desktop sidebar: 64px collapsed, 220px expanded */}
      <aside
        className="ws-shell-sidebar"
        style={{
          width: collapsed ? 64 : 220,
          flex: `0 0 ${collapsed ? 64 : 220}px`,
          background: 'var(--ink)',
          display: 'flex',
          flexDirection: 'column',
          padding: collapsed ? '18px 8px' : '18px 12px',
          gap: 20,
          transition: 'width 0.22s cubic-bezier(0.16, 1, 0.3, 1), padding 0.22s ease',
          overflow: 'hidden',
          userSelect: 'none',
        }}
      >
        {/* Brand header & toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between', padding: '0 4px', minHeight: 32 }}>
          <Link
            to="/"
            title="Whiskey Sensei Home"
            style={{ display: 'flex', alignItems: 'center', gap: 9, textDecoration: 'none', color: 'inherit', minWidth: 0 }}
          >
            <WhiskeyLogo size={20} />
            {!collapsed && (
              <span className="ws-display" style={{ fontWeight: 700, fontSize: 14.5, letterSpacing: '-0.01em', color: 'var(--text-on-dark)', whiteSpace: 'nowrap' }}>
                Whiskey Sensei
              </span>
            )}
          </Link>
          {!collapsed && (
            <button
              onClick={toggleSidebar}
              title="Collapse sidebar"
              aria-label="Collapse sidebar"
              style={{
                background: 'oklch(1 0 0 / 0.08)',
                border: 'none',
                borderRadius: 6,
                width: 26,
                height: 26,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'var(--text-on-dark-dim)',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 16l-6-6 6-6" />
              </svg>
            </button>
          )}
        </div>

        {/* Primary nav items */}
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              title={collapsed ? item.name : undefined}
              className="ws-nav-link"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                gap: 10,
                padding: collapsed ? '10px 0' : '9px 10px',
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 13.5,
                textDecoration: 'none',
                background: isActive(item.path) ? 'oklch(1 0 0 / 0.12)' : 'transparent',
                color: isActive(item.path) ? 'var(--text-on-dark)' : 'var(--text-on-dark-dim)',
                position: 'relative',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: isActive(item.path) ? 'var(--ember)' : 'inherit' }}>
                {item.icon}
              </span>
              {!collapsed && (
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.name}
                </span>
              )}
            </Link>
          ))}
        </nav>

        {/* Secondary items (only when expanded, or icon indicator when collapsed) */}
        {!collapsed ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-on-dark-dim)', padding: '4px 8px', opacity: 0.7 }}>
              More
            </span>
            {SECONDARY_ITEMS.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="ws-nav-link"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 10px',
                  borderRadius: 6,
                  fontWeight: 500,
                  fontSize: 12.5,
                  textDecoration: 'none',
                  background: isActive(item.path) ? 'oklch(1 0 0 / 0.08)' : 'transparent',
                  color: isActive(item.path) ? 'var(--text-on-dark)' : 'var(--text-on-dark-dim)',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.name}
              </Link>
            ))}
          </div>
        ) : (
          <div style={{ height: 1, background: 'oklch(1 0 0 / 0.08)', margin: '4px 6px' }} />
        )}

        {/* User profile & footer */}
        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {collapsed && (
            <button
              onClick={toggleSidebar}
              title="Expand sidebar"
              aria-label="Expand sidebar"
              style={{
                background: 'oklch(1 0 0 / 0.06)',
                border: 'none',
                borderRadius: 8,
                padding: '8px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'var(--text-on-dark-dim)',
                width: '100%',
              }}
            >
              <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 4l6 6-6 6" />
              </svg>
            </button>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: 10,
              padding: collapsed ? '6px 0' : '8px 10px',
              borderRadius: 8,
              background: 'oklch(1 0 0 / 0.05)',
            }}
          >
            <div
              title={`${user ? [user.firstName, user.lastName].filter(Boolean).join(' ') : 'Guest'} · Level ${level}`}
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: 'var(--ember)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 11,
                color: 'white',
                flexShrink: 0,
              }}
              className="ws-display"
            >
              {initials || '?'}
            </div>
            {!collapsed && (
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-on-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user ? [user.firstName, user.lastName].filter(Boolean).join(' ') : 'Guest'}
                </span>
                <span style={{ fontSize: 10.5, color: 'var(--text-on-dark-dim)' }}>Level {level}</span>
              </div>
            )}
            {!collapsed && signOut && (
              <button
                onClick={signOut}
                title="Log out"
                aria-label="Log out"
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-on-dark-dim)',
                  cursor: 'pointer',
                  display: 'flex',
                  padding: 4,
                  borderRadius: 4,
                }}
              >
                <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 3v7" /><path d="M6 5.5a6 6 0 108 0" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Main content pane */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Desktop top bar with fast sidebar toggle button */}
        <div
          className="ws-shell-topbar-desktop"
          style={{
            height: 54,
            flex: '0 0 54px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 24px',
            background: 'var(--surface)',
            gap: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={toggleSidebar}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-label="Toggle navigation"
              style={{
                background: 'var(--surface-sunken)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'var(--text-secondary)',
                flexShrink: 0,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="14" height="14" rx="2" />
                <path d="M8 3v14" />
              </svg>
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface-sunken)', border: '1px solid var(--border)', borderRadius: 6, padding: '7px 12px', width: 280 }}>
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="var(--text-tertiary)" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="9" r="5.5" /><path d="M17 17l-3.5-3.5" />
              </svg>
              <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>Search frameworks, lessons, concepts&hellip;</span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <Link
              to="/frameworks"
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--ember-strong)',
                textDecoration: 'none',
                padding: '4px 10px',
                background: 'var(--ember-soft)',
                border: '1px solid var(--ember)',
                borderRadius: 999,
              }}
            >
              ⚡ 10 Frameworks Live
            </Link>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--ember)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, color: 'white' }} className="ws-display">
              {initials || '?'}
            </div>
          </div>
        </div>

        {/* Compact mobile top bar */}
        <div className="ws-shell-mobile-topbar" style={{ position: 'sticky', top: 0, zIndex: 5, height: 52, flex: '0 0 52px', borderBottom: '1px solid var(--border)', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', background: 'var(--surface)' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 7, textDecoration: 'none' }}>
            <WhiskeyLogo size={18} />
            <span className="ws-display" style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>Whiskey Sensei</span>
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--ember)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 10.5, color: 'white' }} className="ws-display">
              {initials || '?'}
            </div>
          </div>
        </div>

        <main className="ws-shell-main" style={{ flex: 1, overflow: 'auto', padding: 28 }}>{children}</main>
      </div>

      {/* Fixed bottom tab bar for mobile */}
      <div className="ws-shell-bottom-nav" style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 10, height: 64, alignItems: 'stretch', background: 'var(--surface)', borderTop: '1px solid var(--border)' }}>
        {NAV_ITEMS.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              textDecoration: 'none',
              fontSize: 10.5,
              fontWeight: 600,
              color: isActive(item.path) ? 'var(--ember-strong)' : 'var(--text-tertiary)',
            }}
          >
            {item.icon}
            {item.name}
          </Link>
        ))}
      </div>

      <WhiskeyChat />
    </div>
  );
};

export default AppShell;
